import { DEFAULT_ICONS } from './launcherPersistenceService';

export const sanitizeLauncherIcons = (items) =>
  (Array.isArray(items) ? items : []).filter((it) => String(it?.id || '') !== 'settings');

export const normalizeIcons = (items, fallback = DEFAULT_ICONS) => {
  const sanitized = sanitizeLauncherIcons(items);
  const result = sanitized.map((it, idx) => ({
    ...it,
    position: Number.isInteger(it.position) ? it.position : idx,
  }));
  return result.length ? result : fallback;
};

export const computeSlots = (icons, gridMax) => {
  const arr = new Array(gridMax).fill(null);
  const placeAt = (icon, pos) => {
    const clamped = Math.max(0, Math.min(gridMax - 1, Number.isInteger(pos) ? pos : -1));
    if (clamped >= 0 && !arr[clamped]) { arr[clamped] = icon; return; }
    for (let i = 0; i < gridMax; i++) { if (!arr[i]) { arr[i] = icon; return; } }
  };
  icons.forEach((icon) => placeAt(icon, icon.position));
  return arr;
};

export function nextHomeIconPosition(icons, gridMax, requested) {
  const used = new Set((Array.isArray(icons) ? icons : []).map((it) => Number(it?.position ?? -1)));
  const asked = Number(requested);
  if (Number.isInteger(asked) && asked >= 0 && asked < gridMax && !used.has(asked)) {
    return asked;
  }
  let pos = 0;
  while (used.has(pos) && pos < gridMax) pos++;
  return pos;
}

export const createNewIcon = (icons, gridMax, requestedPosition) => {
  const nid = `btn_${Date.now()}`;
  const pos = nextHomeIconPosition(icons, gridMax, requestedPosition);
  return { id: nid, label: 'New Button', flowId: null, actionType: 'none', position: pos };
};

export const applyIconEdit = (icons, iconId, changes) => {
  const {
    label, flowId, actionId, actionType, pagingZoneId, micDeviceId,
    sipPagingZoneId, imageDisplayActionId, collaborationWorkspaceId,
    contactListId,
    iconType, iconKey, iconText, iconBg, iconImageAssetId,
    labelColor,
  } = changes;
  return icons.map((it) => {
    if (it.id !== iconId) return it;
    const nextType = iconType || it.iconType || 'builtin';
    return {
      ...it,
      label,
      flowId,
      actionId,
      actionType,
      pagingZoneId,
      micDeviceId,
      sipPagingZoneId,
      imageDisplayActionId,
      collaborationWorkspaceId,
      contactListId,
      iconType: nextType,
      iconKey:  nextType === 'builtin' ? (iconKey  || null) : null,
      iconText: nextType === 'text'    ? (iconText || null) : null,
      iconBg:   nextType === 'text'    ? (iconBg   || null) : null,
      iconImageAssetId: nextType === 'image' ? (iconImageAssetId || null) : null,
      labelColor: labelColor || null,
    };
  });
};

export const removeIcon = (icons, iconId) =>
  icons.filter((it) => it.id !== iconId);

/**
 * Move one Home tile to an empty slot or swap with an occupant.
 * Does not compact or reflow other tiles.
 *
 * @returns {{ icons: array, changed: boolean }}
 */
export function applyHomeIconMove(icons, { movedId, overId, gridMax }) {
  const list = Array.isArray(icons) ? icons : [];
  const current = list.find((item) => item && item.id === movedId);
  if (!current || overId == null || overId === '') {
    return { icons: list, changed: false };
  }

  const fromPos = Number(current.position ?? 0);
  let toPos = fromPos;
  const overKey = String(overId);

  if (overKey.startsWith('slot-')) {
    const pos = Number(overKey.slice(5));
    if (!Number.isNaN(pos) && Number.isFinite(gridMax)) {
      toPos = Math.max(0, Math.min(gridMax - 1, pos));
    }
  } else {
    const overIcon = list.find((item) => item && item.id === overKey);
    if (overIcon) toPos = Number(overIcon.position ?? 0);
  }

  if (toPos === fromPos) return { icons: list, changed: false };

  const updated = list.map((item) => ({ ...item }));
  const occupantIdx = updated.findIndex((item) => Number(item.position ?? -1) === toPos);
  const moverIdx = updated.findIndex((item) => item.id === movedId);
  if (moverIdx < 0) return { icons: list, changed: false };

  if (occupantIdx >= 0) {
    const tmp = updated[occupantIdx].position;
    updated[occupantIdx].position = updated[moverIdx].position;
    updated[moverIdx].position = tmp;
  } else {
    updated[moverIdx].position = toPos;
  }

  return { icons: updated, changed: true };
}
